import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { pageURLs } from "@/constants";
import { useCurrentWebsite } from "@/hooks/useCurrentWebsite";
import {
  onboardingStage,
  useMarkOnboardingMoment,
  useOnboardingProgress,
} from "@/hooks/useOnboardingProgress";
import { MEMBER_TOUR_START_EVENT, openSetupGuide } from "@/components/SetupGuide/steps";
import { getUserId, getUserType, setLocalStorage } from "@/helpers/common";
import { getPropertyIcon } from "@/helpers/getPropertyIcons";
import { getMapStyleRef } from "@/helpers/mapbox";
import { wantAdBudgetValue } from "@/helpers/createWantAd";
import AgentProfileCard from "@/components/Common/AgentProfileCard";
import { USER_TYPE_AGENT, WANT_AD_KEY } from "@/constants/common";
import { useAppConfig } from "@/appConfig";
import earthNight from "@/images/earth-night-north-america.webp";
import { iconOf, useTourSamples } from "./samples";
import { isTourPreviewFrame, sampleAgentOf, useViewer } from "./seeds";
import { useGlide } from "@/components/GuidedSpotlight/glide";
import { gitchaExplainerUrl, trackGitchaExplainerClick } from "@/helpers/gitchaExplainer";
import { ConnectPreview, DemandStackPreview, FeedCardPreview, InventoryRowPreview, WantListingPagePreview } from "./RealPreview";
import LocationOptions from "./LocationOptions";
import { trackEvent } from "@/services/analytics";
import {
  getTourState,
  isTourSnoozed,
  readActiveTour,
  readTourFocus,
  readTourResume,
  saveTourFocus,
  saveTourResume,
  setTourState as setState,
  snoozeTour,
  useTourState } from "./store";
import { buildSteps, FOCUS_CHOICES, NEW_WAY, OLD_WAY, oldWayOf, seedSectionOf, SECTION_TITLES, tourLengthTitle } from "./steps";
import { ConnectedSphere, FragmentedNetwork } from "./NetworkVisual";
import "./MemberTour.scss";

const TOUR_PAGE = 1003;
const ROUTE_TIMEOUT_MS = 8000;

const reducedMotion = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
};

export { isTourSlug, useTourSeed, useTourSampleWantAd, TOUR_SAMPLE_SLUG, TOUR_NETWORK_SLUG } from "./seeds";

const EARTH = { west: -128, east: -62, north: 52, south: 16 };
const MAP_STYLE = "mapbox/dark-v11";

const marketOf = (tenant) => {
  const market = tenant?.defaultMarket || {};
  const lat = Number(market.latitude);
  const lng = Number(market.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng)
    ? { lat, lng, city: market.city || "", zoom: Number(market.zoom) }
    : null;
};

const earthPoint = (market) => {
  const x = (market.lng - EARTH.west) / (EARTH.east - EARTH.west);
  const y = (EARTH.north - market.lat) / (EARTH.north - EARTH.south);
  return { x: Math.min(Math.max(x, 0), 1), y: Math.min(Math.max(y, 0), 1) };
};

const mapUrlOf = (market) => {
  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  if (!token || !market) return null;
  return `https://api.mapbox.com/styles/v1/${MAP_STYLE}/static/${market.lng},${market.lat},${zoomOf(market)},0/1280x720@2x?access_token=${token}&attribution=false&logo=false`;
};

const DESCEND_SCALE = 5.5;
const EARTH_DEGREES = EARTH.east - EARTH.west;

const midZoomOf = () => {
  const width = window.innerWidth || 1440;
  const height = window.innerHeight || 900;
  const earthWidth = Math.max(width * 1.15, height * 2.11);
  const pxPerDegree = (earthWidth * DESCEND_SCALE) / EARTH_DEGREES;
  const cover = Math.max(width / 1280, height / 1280);
  const zoom = Math.log2((pxPerDegree * 360) / (512 * cover));
  return Math.min(Math.max(zoom, 3), 12).toFixed(2);
};

const midUrlOf = (market) => {
  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  if (!token || !market) return null;
  return `https://api.mapbox.com/styles/v1/${MAP_STYLE}/static/${market.lng},${market.lat},${midZoomOf()},0/1280x1280@2x?access_token=${token}&attribution=false&logo=false`;
};

const thumbUrlOf = (market) => {
  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  if (!token || !market) return null;
  const { owner, styleId } = getMapStyleRef();
  return `https://api.mapbox.com/styles/v1/${owner}/${styleId}/static/${market.lng},${market.lat},10.2,0/240x240@2x?access_token=${token}&attribution=false&logo=false`;
};

const WantListingCard = ({ market, sample }) => {
  const thumb = thumbUrlOf(market);
  const { tenant } = useAppConfig() || {};
  const agent = sampleAgentOf(tenant);
  return (
    <div className="orbit-scene__wl">
      <div
        className="orbit-scene__wl-map"
        style={thumb ? { backgroundImage: `url(${thumb})` } : undefined}
      >
        <span className={`orbit-scene__wl-radius${sample.area ? " is-drawn" : ""}`} />
        <div className="property-tile w-auto">
          <span className="property-tile-label">Buyer</span>
          <img src={getPropertyIcon(1)} alt="" className="property-icon-img" />
        </div>
      </div>
      <div className="orbit-scene__wl-info">
        <span className="prize-range-badge">{wantAdBudgetValue(sample.budgetMin, sample.budgetMax)}</span>
        <p className="orbit-scene__wl-title">{sample.title.replace(/(\w)-(\w)/g, "$1-\u2060$2")}</p>
        <p className="orbit-scene__wl-where">
          {sample.area
            ? `Custom area in ${market?.city || "your market"}`
            : `Within ${sample.radiusMiles} mi of ${market?.city || "your market"}`}
        </p>
        <p className="orbit-scene__wl-specs">
          {sample.bedrooms}+ <span>beds</span>
          <i>|</i>
          {sample.bathrooms}+ <span>baths</span>
          <i>|</i>
          {sample.sqftMin.toLocaleString("en-US")} - {sample.sqftMax.toLocaleString("en-US")} <span>sqft</span>
        </p>
        <div className="orbit-scene__wl-tags">
          {sample.niceToHaves.map(({ Icon, Text }) => (
            <div className="property-tag mb-0" key={Text}>
              <div className="tag-icon">
                <img src={iconOf(Icon)} alt="" />
              </div>
              <span>{Text}</span>
            </div>
          ))}
        </div>
        <div className="orbit-scene__wl-agent">
          <AgentProfileCard
            isAgentCreated
            isAgentPremium
            userAvatar={agent.avatar}
            userFirstName={agent.firstName}
            userLastName={agent.lastName}
            userCompany={agent.company}
            avatarSize="28px"
          />
        </div>
      </div>
    </div>
  );
};

const zoomOf = (market) => (Number.isFinite(market?.zoom) ? market.zoom : 11.4);

const BROKERAGES = [
  [[800, 104], [744, 452]],
  [[990, 84], [1140, 120]],
  [[1196, 300], [1170, 500]],
  [[950, 532], [830, 512]],
];
const OFFICES = BROKERAGES.flat();
const CARD = { x: 764, y: 181, w: 380, h: 256 };
const HUB = [CARD.x + CARD.w / 2, CARD.y + CARD.h / 2];
const REPLY = BROKERAGES[3][0];

const Member = ({ x, y, index }) => (
  <g className="orbit-scene__member" style={{ "--i": index }} transform={`translate(${x} ${y})`}>
    <circle r="15" />
    <circle className="orbit-scene__person" cy="-3.5" r="3.6" />
    <path className="orbit-scene__person" d="M-7 8.5a7 6.5 0 0 1 14 0Z" />
  </g>
);

const OrbitScene = ({ market, sample }) => {
  const point = market ? earthPoint(market) : { x: 0.4, y: 0.5 };
  const mapUrl = mapUrlOf(market);
  const midUrl = midUrlOf(market);
  let order = 0;
  return (
    <div className="orbit-scene" aria-hidden="true" style={{ "--x": point.x, "--y": point.y }}>
      <img className="orbit-scene__earth" src={earthNight} alt="" />
      {midUrl ? <img className="orbit-scene__mid" src={midUrl} alt="" /> : null}
      {mapUrl ? <img className="orbit-scene__map" src={mapUrl} alt="" /> : null}
      <svg className="orbit-scene__layer" viewBox="0 0 1280 720" preserveAspectRatio="xMidYMid slice">
        <g className="orbit-scene__siblings">
          {BROKERAGES.map((offices) =>
            offices.slice(1).map(([x, y], index) => (
              <line key={`${x}-${y}`} x1={offices[index][0]} y1={offices[index][1]} x2={x} y2={y} />
            ))
          )}
        </g>
        <g className="orbit-scene__links">
          {OFFICES.map(([x, y], index) => (
            <line key={`${x}-${y}`} x1={HUB[0]} y1={HUB[1]} x2={x} y2={y} style={{ "--i": index }} />
          ))}
        </g>
        <line className="orbit-scene__reply" x1={REPLY[0]} y1={REPLY[1]} x2={HUB[0]} y2={HUB[1]} />
        {BROKERAGES.map((offices) =>
          offices.map(([x, y]) => <Member key={`${x}-${y}`} x={x} y={y} index={order++} />)
        )}
        <foreignObject x={CARD.x} y={CARD.y} width={CARD.w} height={CARD.h}>
          <WantListingCard market={market} sample={sample} />
        </foreignObject>
      </svg>
      <div className="orbit-scene__scrim" />
      {mapUrl ? <span className="orbit-scene__credit">© Mapbox © OpenStreetMap · Earth: NASA Earth Observatory</span> : null}
    </div>
  );
};

const logosOf = (tenant) => {
  const logos = tenant?.assets?.logos || {};
  const white = logos.logoWhite || logos.logoV1 || null;
  return { src: white, needsWhite: Boolean(white) && white === logos.logoV1, name: tenant?.mlsShortName || "" };
};

const organizationOf = (tenant) => {
  const name = tenant?.organizationName || tenant?.mlsShortName || "";
  if (!name) return { article: "", name: "" };
  const words = name.split(" ");
  const last = words.pop();
  return {
    article: tenant?.organizationArticle || "",
    name: words.length ? `${words.join(" ")}\u00a0${last}` : last,
  };
};

const Cutscene = ({ onStart, onSkip, market, tenant, sample }) => {
  const [shown, setShown] = useState(false);
  const rootRef = useRef(null);
  const mls = logosOf(tenant);
  const organization = organizationOf(tenant);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    rootRef.current?.focus();
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      className={`member-tour-cut${shown ? " is-shown" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Welcome to the Buyer Listing Service"
    >
      <OrbitScene market={market} sample={sample} />
      <div className="member-tour-cut__inner">
        <div className="member-tour-cut__brand member-tour-cut__line">
          <img src="/assets/bls/bls-logo-white.png" alt="The Buyer Listing Service" />
          {mls.src ? (
            <>
              <span className="member-tour-cut__by" aria-hidden="true">×</span>
              <img
                src={mls.src}
                alt={mls.name}
                className={mls.needsWhite ? "is-forced-white" : undefined}
              />
            </>
          ) : null}
        </div>
        <h2 className="member-tour-cut__line">
          <span className="member-tour-cut__loud">
            Welcome to the <em>Buy&nbsp;Side</em>
          </span>
          {organization.name ? (
            <span className="member-tour-cut__org">
              <span className="member-tour-cut__of">of{organization.article ? ` ${organization.article}` : ""}</span>{" "}
              <strong>
                {organization.name.split("®").map((part, index) => (
                  <React.Fragment key={part + index}>
                    {index > 0 ? <sup>®</sup> : null}
                    {part}
                  </React.Fragment>
                ))}
              </strong>
            </span>
          ) : null}
        </h2>
        <p className="member-tour-cut__line is-second">
          <span className="member-tour-cut__promise">More market transparency.</span>
          <span className="member-tour-cut__promise">More transactional opportunity.</span>
          <span className="member-tour-cut__promise">A stronger agent&nbsp;network.</span>
        </p>
        <div className="member-tour-cut__actions member-tour-cut__line is-third">
          <button type="button" className="member-tour-cut__go" onClick={onStart}>
            <span>{tourLengthTitle()}</span>
            <svg className="member-tour-cut__arrow" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
              <path d="M4 10h11M11 5.5 15.5 10 11 14.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button type="button" className="member-tour-cut__later" onClick={onSkip}>
            Remind me later
          </button>
        </div>
      </div>
    </div>
  );
};

const TIP_WIDTH = 360;

const typeset = (text = "") =>
  text.replace(/(\w)-(?=\w)/g, "$1\u2011").replace(/ (\S+)$/, "\u00a0$1");

const emphasize = (text = "") =>
  typeset(text)
    .split(/\*\*(.+?)\*\*/)
    .map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));

const GAP = 16;
const WAIT_MS = 4000;

const isRendered = (el) => {
  for (let node = el; node && node !== document.body; node = node.parentElement) {
    if (getComputedStyle(node).display === "none") return false;
  }
  return true;
};

const useTarget = (selector, pathname) => {
  const [target, setTarget] = useState(null);
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    if (!selector) {
      setTarget(null);
      setSettled(true);
      return undefined;
    }
    setSettled(false);
    const started = Date.now();
    let timer;
    const look = () => {
      const matches = [...document.querySelectorAll(selector)].filter(isRendered);
      const found =
        matches.find((el) => {
          const box = el.getBoundingClientRect();
          return box.width > 0 && box.height > 0;
        }) || (matches.length === 1 ? matches[0] : null);
      if (found) {
        setTarget(found);
        setSettled(true);
        return;
      }
      if (Date.now() - started > WAIT_MS) {
        setTarget(null);
        setSettled(true);
        return;
      }
      timer = setTimeout(look, 120);
    };
    look();
    return () => clearTimeout(timer);
  }, [selector, pathname]);
  return { target, settled };
};

const REVERSE_ROWS = [
  ["When", "After the property is listed", "Before and after you list in the MLS"],
  ["What it reads", "Private saved searches", "Buyer Want-Listings published by agents"],
  ["What you get", "A match count and an email blast, with no context", "Every buyer's budget, timing, must-haves and nice-to-haves"],
  ["What you can do", "Guess which buyers are real", "Demand insights, a Buyer Match Report and a direct line to each buyer's agent"],
];

const ReverseCompare = () => (
  <div className="member-tour-compare-mini" role="table" aria-label="Reverse prospecting compared with the Buyer Listing Service">
    <div className="member-tour-compare-mini__row member-tour-compare-mini__row--head" role="row">
      <span role="columnheader" />
      <span role="columnheader">Reverse prospecting</span>
      <span role="columnheader">Buyer Listing Service®</span>
    </div>
    {REVERSE_ROWS.map(([label, old, now]) => (
      <div className="member-tour-compare-mini__row" role="row" key={label}>
        <span role="rowheader">{label}</span>
        <span role="cell">{old}</span>
        <span role="cell" className="is-ours">{now}</span>
      </div>
    ))}
  </div>
);

const Spotlight = ({ item, pathname, progress, onNext, onBack, onEnd, nextLabel, canGoBack }) => {
  const { target, settled } = useTarget(item.target, pathname);
  const { rect, moving } = useGlide(target, pathname);
  const tipRef = useRef(null);
  const [tipSize, setTipSize] = useState({ width: TIP_WIDTH, height: 200 });

  useLayoutEffect(() => {
    if (tipRef.current) {
      const box = tipRef.current.getBoundingClientRect();
      setTipSize((prev) => (prev.width === box.width && prev.height === box.height ? prev : { width: box.width, height: box.height }));
    }
  }, [item, moving, Boolean(rect)]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onEnd();
      if (event.key === "ArrowRight") onNext();
      if (event.key === "ArrowLeft" && canGoBack) onBack();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [item, onNext, onBack, onEnd, canGoBack]);

  if (!settled && !rect) return <div className="member-tour" role="presentation"><div className="member-tour__dim" /></div>;

  const pad = 8;
  const viewport = { width: window.innerWidth, height: window.innerHeight };
  let tip = { top: viewport.height / 2 - tipSize.height / 2, left: viewport.width / 2 - tipSize.width / 2 };
  if (rect) {
    const right = rect.left + rect.width + pad + GAP;
    const left = rect.left - pad - GAP - tipSize.width;
    if (right + tipSize.width < viewport.width - GAP && rect.width < viewport.width * 0.5) {
      tip = { top: rect.top, left: right };
    } else if (left > GAP && rect.width < viewport.width * 0.5) {
      tip = { top: rect.top, left };
    } else {
      tip = { top: rect.top + rect.height + pad + GAP, left: rect.left };
    }
    if (tip.top + tipSize.height > viewport.height - GAP) {
      tip.top = Math.max(GAP, rect.top - pad - GAP - tipSize.height);
    }
    tip.left = Math.min(Math.max(GAP, tip.left), viewport.width - tipSize.width - GAP);
    tip.top = Math.max(GAP, tip.top);
  }

  return (
    <div
      className={`member-tour${item.target?.includes("sell-demand") ? " is-over-drawer" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="member-tour-title"
    >
      {rect ? (
        <div
          className="member-tour__spot"
          style={{
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
          }}
        />
      ) : (
        <div className="member-tour__dim" />
      )}
      <div
        className={`member-tour__tip${item.extra ? " member-tour__tip--wide" : ""}${moving ? " is-moving" : ""}`}
        ref={tipRef}
        style={{ top: tip.top, left: tip.left }}
      >
        <div className="member-tour__tip-body" key={item.title}>
        <p className="member-tour__kicker">
          {SECTION_TITLES[item.section]}
          {progress ? ` · ${progress.at} of ${progress.of}` : ""}
        </p>
        <h3 id="member-tour-title">{typeset(item.title)}</h3>
        <p className="member-tour__body">{emphasize(item.body)}</p>
        {item.link === "gitcha" ? (
          <a
            className="member-tour__link"
            href={gitchaExplainerUrl("member_tour")}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackGitchaExplainerClick("member_tour")}
          >
            How Gitcha.com works for buyer&nbsp;agents&nbsp;&rarr;
          </a>
        ) : null}
        {item.extra === "location-options" ? <LocationOptions /> : null}
        {item.extra === "reverse-compare" ? <ReverseCompare /> : null}
        <div className={`member-tour__nav${nextLabel === "Next" ? "" : " member-tour__nav--finish"}`}>
          {progress ? (
            <span className="member-tour__dots" aria-hidden="true">
              {Array.from({ length: progress.of }, (_, index) => (
                <i key={index} className={index === progress.at - 1 ? "is-on" : ""} />
              ))}
            </span>
          ) : null}
          <button type="button" className="member-tour__ghost" onClick={onEnd}>
            End tour
          </button>
          {canGoBack ? (
            <button type="button" className="member-tour__ghost" onClick={onBack}>
              Back
            </button>
          ) : null}
          <button type="button" className="member-tour__go" onClick={onNext} autoFocus>
            {typeset(nextLabel)}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

const POINT_ICONS = {
  listing: "M6 3.5h8l3 3V20a.5.5 0 0 1-.5.5h-10A.5.5 0 0 1 6 20V3.5ZM9 10h6M9 13.5h6M9 17h4",
  broadcast: "M12 13.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM8.5 8.5a5 5 0 0 0 0 7M15.5 8.5a5 5 0 0 1 0 7M5.5 5.5a9 9 0 0 0 0 13M18.5 5.5a9 9 0 0 1 0 13",
  service: "M4 8.5h16v11H4v-11ZM9 8.5V6a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 6v2.5M4 13h16",
  demand: "M5 19.5V13M10 19.5V9M15 19.5v-5M20 19.5V5",
  report: "M6 3.5h12V20.5H6V3.5ZM9 8h6M9 11.5h6M9 15h3.5",
  bell: "M6.5 16.5V11a5.5 5.5 0 0 1 11 0v5.5l1.5 2h-14l1.5-2ZM10 20.5a2 2 0 0 0 4 0",
  message: "M4.5 18.5V6.5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2V14a2 2 0 0 1-2 2H8l-3.5 2.5Z",
  send: "M20 4 10.5 13.5M20 4l-6 16-3.5-6.5L4 10l16-6Z",
  shield: "M12 3.5 19 6v5.5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-2.5ZM9 12l2 2 4-4",
  price: "M3.5 12.5 11 5h8v8l-7.5 7.5-8-8ZM15.5 8.5h.01",
};

const PointIcon = ({ name }) => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
    <path
      d={POINT_ICONS[name]}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TourVisual = ({ kind, samples, scale }) =>
  kind === "page" ? (
    <WantListingPagePreview />
  ) : kind === "demand" ? (
    <DemandStackPreview samples={samples} />
  ) : kind === "connect" ? (
    <ConnectPreview samples={samples} />
  ) : kind === "inventory" ? (
    <InventoryRowPreview samples={samples} scale={scale} />
  ) : (
    <FeedCardPreview samples={samples} scale={scale} width={440} vertical />
  );

const FocusQuestion = ({ samples, onChoose, onSkip }) => {
  const [shown, setShown] = useState(false);
  const { firstName } = useViewer();
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    const onKey = (event) => {
      if (event.key === "Escape") onSkip();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(id);
      document.removeEventListener("keydown", onKey);
    };
  }, [onSkip]);
  const previews = {
    buy: <FeedCardPreview samples={samples} scale={0.26} width={760} />,
    sell: <InventoryRowPreview samples={samples} scale={0.37} crop={530} />,
    both: (
      <div className="tour-real-stack">
        <FeedCardPreview samples={samples} scale={0.25} width={760} />
        <InventoryRowPreview samples={samples} scale={0.3} crop={530} />
      </div>
    ),
  };
  return (
    <div
      className={`member-tour-section${shown ? " is-shown" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="member-tour-focus-title"
    >
      <img className="member-tour-section__logo" src="/assets/bls/bls-logo-white.png" alt="" />
      <h2 className="member-tour-section__headline" id="member-tour-focus-title">
        <span>{firstName ? `Hi ${firstName}, let\u2019s tailor your\u00a0tour` : "Let\u2019s tailor your\u00a0tour"}</span>
      </h2>
      <p className="member-tour-section__subline">
        Who are you working with right now? We&rsquo;ll start the tour there, and you&rsquo;ll still see&nbsp;everything.
      </p>
      <div className="member-tour-focus">
        {FOCUS_CHOICES.map((choice, index) => (
          <div key={choice.id} className="member-tour-focus__choice">
            <div className="member-tour-focus__preview">{previews[choice.id]}</div>
            <button
              type="button"
              className="member-tour-focus__pick"
              onClick={() => onChoose(choice.id)}
              autoFocus={index === 0}
            >
              {choice.title}
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="member-tour-cut__later" onClick={onSkip}>
        Skip, show me everything
      </button>
    </div>
  );
};

const glueHyphens = (line) => line.replace(/-(?=[A-Z])/g, "-\u2060");

const CompareCut = ({ samples, network, onNext, onBack, onEnd }) => {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    const onKey = (event) => {
      if (event.key === "Escape") onEnd();
      if (event.key === "ArrowRight") onNext();
      if (event.key === "ArrowLeft") onBack();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(id);
      document.removeEventListener("keydown", onKey);
    };
  }, [onNext, onBack, onEnd]);
  return (
    <div
      className={`member-tour-section member-tour-compare${shown ? " is-shown" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="member-tour-compare-title"
    >
      <h2 className="member-tour-section__headline" id="member-tour-compare-title">
        <span>The old way vs. the Buyer Listing&nbsp;Service&reg;</span>
      </h2>
      <p className="member-tour-compare__lead">
        Finally connected. Every member and every Buyer Want-Listing strengthens the network, and every connection is another chance to&nbsp;transact.
      </p>
      <div className="member-tour-compare__grid">
        <section className="member-tour-compare__old" aria-label="The old way">
          <p className="member-tour-compare__label">The old way: siloed</p>
          <div className="member-tour-compare__visual">
            <FragmentedNetwork labels={oldWayOf(samples).map((item) => item.channel)} />
          </div>
          <ul>
            {OLD_WAY.map((line, index) => (
              <li key={line} style={{ "--i": index }}>
                {glueHyphens(line)}
              </li>
            ))}
          </ul>
        </section>
        <section className="member-tour-compare__new" aria-label="The new way">
          <p className="member-tour-compare__label">The Buyer Listing Service&reg;: connected</p>
          <div className="member-tour-compare__visual">
            <ConnectedSphere />
          </div>
          <ul>
            {NEW_WAY(network).map((line) => (
              <li key={line}>{glueHyphens(line)}</li>
            ))}
          </ul>
        </section>
      </div>
      <div className="member-tour-section__actions">
        <button type="button" className="member-tour-cut__go" onClick={onNext} autoFocus>
          <span>Get started</span>
          <svg className="member-tour-cut__arrow" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
            <path d="M4 10h11M11 5.5 15.5 10 11 14.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button type="button" className="member-tour-cut__skip" onClick={onBack}>
          Back
        </button>
      </div>
    </div>
  );
};

const SectionCut = ({ item, samples, onNext, onEnd }) => {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    const onKey = (event) => {
      if (event.key === "Escape") onEnd();
      if (event.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(id);
      document.removeEventListener("keydown", onKey);
    };
  }, [onNext, onEnd]);
  return (
    <div
      className={`member-tour-section member-tour-section--split${shown ? " is-shown" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label={item.headline.replace("\n", " ")}
    >
      <div className="member-tour-section__text">
        <img className="member-tour-section__logo" src="/assets/bls/bls-logo-white.png" alt="" />
        <h2 className="member-tour-section__headline">
          {item.headline.split("\n").map((line) => (
            <span key={line}>{line}</span>
          ))}
        </h2>
        <ul className="member-tour-points">
          {item.points.map((point, index) => (
            <li key={point.title} style={{ "--i": index }}>
              <span className="member-tour-points__icon">
                <PointIcon name={point.icon} />
              </span>
              <span>
                <strong>{point.title}</strong>
                <span>{point.line}</span>
              </span>
            </li>
          ))}
        </ul>
        {item.note ? <p className="member-tour-points__note">{item.note}</p> : null}
        <div className="member-tour-section__actions">
          <button type="button" className="member-tour-cut__go" onClick={onNext} autoFocus>
            <span>Continue</span>
            <svg className="member-tour-cut__arrow" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
              <path d="M4 10h11M11 5.5 15.5 10 11 14.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button type="button" className="member-tour-cut__skip" onClick={onEnd}>
            End tour
          </button>
        </div>
      </div>
      <div className="member-tour-section__visual">
        <TourVisual kind={item.visual} samples={samples} scale={item.visual === "inventory" ? 0.7 : 0.82} />
      </div>
    </div>
  );
};

const progressOf = (steps, index) => {
  const item = steps[index];
  if (!item || item.kind !== "step") return null;
  const peers = steps.filter((other) => other.kind === "step" && other.section === item.section);
  return { at: peers.indexOf(item) + 1, of: peers.length };
};

const MemberTour = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isGitchaDefaultWebsite } = useCurrentWebsite();
  const previewFrame = isTourPreviewFrame();
  const enabled =
    !previewFrame &&
    Boolean(getUserId()) &&
    !isGitchaDefaultWebsite &&
    Number(getUserType()) === USER_TYPE_AGENT;
  const { data: progress } = useOnboardingProgress(enabled);
  const { tenant, flags } = useAppConfig() || {};
  const mlsSync = Boolean(flags?.mlsListings);
  const markMoment = useMarkOnboardingMoment();
  const { phase, step, focus: chosenFocus } = useTourState();
  const samples = useTourSamples(enabled && phase !== "idle");
  const userId = getUserId();
  const focus = chosenFocus || readTourFocus(userId);
  const network = tenant?.mlsShortName || "MLS";

  const steps = useMemo(() => buildSteps({ network, focus, mlsSync }), [network, focus, mlsSync]);
  const item = phase === "steps" ? steps[step] : null;
  const wantsFeedDrawer = Boolean(item?.target?.includes("sell-demand"));

  useEffect(() => {
    if (getTourState().feedDrawer !== wantsFeedDrawer) setState({ feedDrawer: wantsFeedDrawer });
  }, [wantsFeedDrawer]);

  const start = useCallback(
    (event) => {
      if (!enabled) return;
      const resume = event?.detail?.resume ? readTourResume(userId) : null;
      saveTourResume(userId, null);
      trackEvent({
        name: "member_tour_started",
        attributes: { trigger: event ? "requested" : "first_visit", resumed: Boolean(resume) },
      });
      window.scrollTo({ top: 0 });
      if (resume && resume.step < steps.length) {
        setState({ phase: "steps", step: resume.step, seed: null, owner: userId, arrived: null });
        return;
      }
      if (window.location.pathname !== pageURLs.home) navigate(pageURLs.home);
      setState({ phase: "cut", step: 0, seed: null, owner: userId, arrived: null });
    },
    [enabled, navigate, userId, steps.length],
  );

  // Only counts as seen once the agent takes the tour or finishes it, so "Remind me later" brings it back.
  const seenMarked = useRef(false);
  const markSeen = useCallback(() => {
    if (!progress || progress.tourSeen || seenMarked.current) return;
    seenMarked.current = true;
    markMoment.mutate(progress.pages?.tour || TOUR_PAGE, {
      onError: () => {
        seenMarked.current = false;
      },
    });
  }, [progress, markMoment]);

  // A different agent signing in without a reload gets their own auto-start and seen mark.
  const lastUserId = useRef(userId);
  useEffect(() => {
    if (lastUserId.current === userId) return;
    lastUserId.current = userId;
    seenMarked.current = false;
    setState({ autoStarted: false });
  }, [userId]);

  useEffect(() => {
    if (!previewFrame) return undefined;
    document.body.classList.add("tour-preview-frame");
    return () => document.body.classList.remove("tour-preview-frame");
  }, [previewFrame]);

  useEffect(() => {
    window.addEventListener(MEMBER_TOUR_START_EVENT, start);
    return () => window.removeEventListener(MEMBER_TOUR_START_EVENT, start);
  }, [start]);

  useEffect(() => {
    if (!enabled || getTourState().phase !== "idle") return;
    const saved = readActiveTour(userId);
    if (!saved) return;
    setState({ autoStarted: true });
    if (saved.phase === "cut" && window.location.pathname !== pageURLs.home) navigate(pageURLs.home);
    setState(saved);
  }, [enabled, userId, navigate]);

  useEffect(() => {
    if (getTourState().autoStarted || !enabled || !progress || getTourState().phase !== "idle") return;
    // Wait for /home so a deep link (an intro or message from an email) is never pulled away.
    if (progress.tourSeen || onboardingStage(progress) !== "new" || pathname !== pageURLs.home) return;
    if (isTourSnoozed(userId)) return;
    setState({ autoStarted: true });
    start();
  }, [enabled, progress, pathname, start, userId]);

  useEffect(() => {
    if (!enabled || phase === "idle") return undefined;
    document.body.classList.add("member-tour-open");
    return () => document.body.classList.remove("member-tour-open");
  }, [enabled, phase]);

  useEffect(() => {
    if (phase !== "steps" || !steps[step]) return;
    trackEvent({
      name: "member_tour_step_viewed",
      attributes: { step, of: steps.length, section: steps[step].section, kind: steps[step].kind, title: steps[step].title || steps[step].headline || null },
    });
  }, [phase, step, steps]);

  useEffect(() => {
    if (phase !== "steps") return;
    const seed = seedSectionOf(steps, step);
    if (getTourState().seed !== seed) setState({ seed });
    if (!item?.page || item.page === pathname || getTourState().arrived === step) return;
    if (item.page.startsWith(pageURLs.wantAdDetails)) {
      setLocalStorage(WANT_AD_KEY, { wantAdId: null, isFromHomeFeed: false });
    }
    window.scrollTo({ top: 0 });
    navigate(item.page);
  }, [phase, step, item, steps, pathname, navigate]);

  const end = useCallback(
    (then, resumeAt = null) => {
      const { phase: endedIn, step: endedAt } = getTourState();
      trackEvent({
        name: "member_tour_ended",
        attributes: {
          outcome: then === "finish" ? "finished" : endedIn === "cut" ? "remind_later" : "left",
          phase: endedIn,
          step: endedAt,
          of: steps.length,
          section: steps[endedAt]?.section || null,
        },
      });
      if (then === "finish") markSeen();
      else if (endedIn === "cut") snoozeTour(userId);
      setState({ phase: "idle", step: 0, seed: null, arrived: null });
      saveTourResume(
        userId,
        resumeAt !== null && resumeAt > 0 && resumeAt < steps.length
          ? { step: resumeAt, section: SECTION_TITLES[steps[resumeAt]?.section] || null }
          : null,
      );
      if (then === "finish" && window.location.pathname !== pageURLs.home) {
        window.scrollTo({ top: 0 });
        navigate(pageURLs.home);
      }
      if (then === "finish") openSetupGuide();
    },
    [navigate, userId, steps, markSeen],
  );

  const next = useCallback(() => {
    const { step: at } = getTourState();
    if (at < steps.length - 1) setState({ step: at + 1 });
    else end("finish");
  }, [steps, end]);

  const back = useCallback(() => {
    let at = getTourState().step - 1;
    while (at > 0 && steps[at]?.kind !== "step") at -= 1;
    setState({ step: Math.max(0, at) });
  }, [steps]);

  useEffect(() => {
    if (phase !== "steps" || !item?.page) return;
    if (item.page === pathname) {
      if (getTourState().arrived !== step) setState({ arrived: step });
      return;
    }
    if (getTourState().arrived !== step) return;
    setState({ arrived: null });
    let at = step - 1;
    while (at >= 0 && !(steps[at].kind === "step" && steps[at].page === pathname)) at -= 1;
    if (at >= 0) setState({ step: at });
    else end(null, step);
  }, [phase, step, item, steps, pathname, end]);

  // The tour locks the page, so if the page it is waiting for never shows up, give the page back.
  useEffect(() => {
    if (!enabled) return undefined;
    const waitingOnStep = phase === "steps" && item?.page && item.page !== pathname;
    const waitingOnHome = phase === "cut" && pathname !== pageURLs.home;
    if (!waitingOnStep && !waitingOnHome) return undefined;
    const timer = setTimeout(() => {
      const now = getTourState();
      if (now.phase !== phase || now.step !== step) return;
      end(null, waitingOnStep ? step : null);
    }, ROUTE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [enabled, phase, step, item, pathname, end]);

  const stop = useCallback(() => {
    const { phase: now, step: at } = getTourState();
    end(null, now === "steps" ? at : null);
  }, [end]);
  const choose = useCallback(
    (picked) => {
      trackEvent({ name: "member_tour_focus", attributes: { focus: picked || "skipped" } });
      saveTourFocus(userId, picked);
      setState({ phase: "steps", step: 0, focus: picked });
    },
    [userId],
  );

  if (!enabled || phase === "idle") return null;

  if (phase === "cut") {
    if (pathname !== pageURLs.home) return null;
    return createPortal(
      <Cutscene
        onStart={() => {
          markSeen();
          setState({ phase: "ask", step: 0 });
        }}
        onSkip={stop}
        market={marketOf(tenant)}
        tenant={tenant}
        sample={samples.wantListing}
      />,
      document.body,
    );
  }

  if (phase === "ask") {
    return createPortal(
      <FocusQuestion samples={samples} onChoose={choose} onSkip={() => choose(null)} />,
      document.body,
    );
  }

  if (!item) return null;
  if (item.kind === "compare") {
    return createPortal(
      <CompareCut key={step} samples={samples} network={network} onNext={next} onBack={back} onEnd={stop} />,
      document.body,
    );
  }
  if (item.kind === "cut") {
    return createPortal(<SectionCut key={step} item={item} samples={samples} onNext={next} onEnd={stop} />, document.body);
  }
  if (item.page && item.page !== pathname) return null;

  const isLast = step === steps.length - 1;
  return createPortal(
    <Spotlight
      item={item}
      pathname={pathname}
      progress={progressOf(steps, step)}
      onNext={next}
      onBack={back}
      onEnd={stop}
      canGoBack={step > 0}
      nextLabel={isLast ? "Finish" : "Next"}
    />,
    document.body,
  );
};

export default MemberTour;
